'use client';

import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/buttons';
import { useInitEmblaCarousel } from '@/components/EmblaCarousel/hooks/useInitEmblaCarousel';
import {
  ScLeftArrowOutside,
  ScOutsideArrows,
  ScRightArrowOutside,
} from '@/components/NativeSlider/components/arrows/OutsideArrows/styled';
import { SectionTitle } from '@/components/SectionTitle';
import type { BonusShopProduct } from '../../types';
import { BonusShopCard } from '../BonusShopCard';
import { ScBonusShopSlider } from './styled';

interface BonusShopSliderProps {
  title: string;
  products: BonusShopProduct[];
  onShowAll: () => void;
}

const EMBLA_OPTIONS = {
  align: 'start',
  containScroll: 'trimSnaps',
  slidesToScroll: 'auto',
} as const;

export const BonusShopSlider: FC<BonusShopSliderProps> = ({
  title,
  products,
  onShowAll,
}) => {
  const { t } = useTranslation('common');

  const { emblaRef, canScrollPrev, canScrollNext, scrollPrev, scrollNext } =
    useInitEmblaCarousel({ options: EMBLA_OPTIONS });

  if (!products.length) return null;

  return (
    <ScBonusShopSlider>
      <div className="slider-header">
        <SectionTitle>{title}</SectionTitle>
        <div className="slider-controls">
          <Button autoPaddingHorizontal onClick={onShowAll} xSmall>
            {t('gameCategories.all')}
          </Button>
          <ScOutsideArrows>
            <ScLeftArrowOutside
              aria-label="arrow left"
              isActive={canScrollPrev}
              onClick={scrollPrev}
            />
            <div className="divider" />
            <ScRightArrowOutside
              aria-label="arrow right"
              isActive={canScrollNext}
              onClick={scrollNext}
            />
          </ScOutsideArrows>
        </div>
      </div>

      <div className="embla-viewport" ref={emblaRef}>
        <div className="embla-container">
          {products.map((product) => (
            <div className="embla-slide" key={product.id}>
              <BonusShopCard product={product} />
            </div>
          ))}
        </div>
      </div>
    </ScBonusShopSlider>
  );
};
